import React from 'react';
import { Button, Spin } from 'antd';
import { PhoneFilled } from '@ant-design/icons';
import { useTranslate } from '@/translations';
import {
  getCallMessagePresentation,
  type ChatMessage,
} from '../matrix/message';
import { useAuthenticatedMedia } from '../hooks/useAuthenticatedMedia';
import { ImageMessage } from './ImageMessage';
import { VoiceNotePlayer } from './VoiceNotePlayer';

interface MessageContentProps {
  message: ChatMessage;
  matrixUserId?: string | null;
  onRetry(eventId: string): void;
  onOpenImage(eventId: string): void;
}

const VoiceContent = ({ message }: { message: ChatMessage }) => {
  const { t } = useTranslate();
  const media = useAuthenticatedMedia(message.mediaMxc);
  if (media.loading) return <Spin size="small" />;
  if (media.failed || !media.url)
    return (
      <span role="alert" className="command-centre-chat__media-state">
        <span>{t('commsMediaUnavailable')}</span>
        <Button
          type="link"
          size="small"
          onClick={media.retry}
          aria-label={t('commsRetryMedia')}
        >
          {t('commsRetryMedia')}
        </Button>
      </span>
    );
  return (
    <VoiceNotePlayer
      src={media.url}
      durationMs={message.durationMs}
      waveform={message.waveform}
    />
  );
};

const CallContent = ({
  message,
  matrixUserId,
}: {
  message: ChatMessage;
  matrixUserId?: string | null;
}) => {
  const { t } = useTranslate();
  const presentation = getCallMessagePresentation(message, matrixUserId);
  if (!presentation) return null;
  return (
    <span className="comms-call-record">
      <PhoneFilled
        aria-hidden="true"
        className={`comms-call-record__icon comms-call-record__icon--${presentation.tone}`}
      />
      <span>{t(presentation.key, presentation.values)}</span>
    </span>
  );
};

export const MessageContent = ({
  message,
  matrixUserId,
  onRetry,
  onOpenImage,
}: MessageContentProps) => {
  const { t } = useTranslate();
  return (
    <div className="comms-message-content">
      {message.kind === 'text' ? (
        <span>{message.body}</span>
      ) : message.kind === 'image' && message.mediaMxc ? (
        <ImageMessage
          eventId={message.eventId}
          mxc={message.mediaMxc}
          thumbnailMxc={message.thumbnailMxc}
          alt={message.body}
          onOpenImage={onOpenImage}
        />
      ) : message.kind === 'call' ? (
        <CallContent message={message} matrixUserId={matrixUserId} />
      ) : (
        <VoiceContent message={message} />
      )}
      {message.sendStatus === 'pending' && (
        <span className="comms-send-status">{t('commsMessageSending')}</span>
      )}
      {message.sendStatus === 'failed' && (
        <div className="comms-send-status" role="alert">
          <span>{t('commsMessageFailed')}</span>
          <Button
            type="link"
            size="small"
            onClick={() => onRetry(message.eventId)}
            aria-label={t('commsRetryMessage')}
          >
            {t('commsRetryMessage')}
          </Button>
        </div>
      )}
    </div>
  );
};
